import { useEffect, useRef, useState } from "react";
import LogoPerception from '../../assets/images/logos/logo-blue-black.png';
import LogoLISI from '../../assets/images/logos/LISI_Logo.png';
import { useSocket } from "../../contexts/SocketContext";
import { formatSecondsToHoursMinutesSeconds, formatSecondsToMinutesSeconds } from "../../helpers/date/date-helper";
import { WrenchScrewdriverIcon } from "@heroicons/react/20/solid";
import { LISILiveSetupPage } from "./lisi-components/LISILiveSetupPage";
import { useParams } from "react-router-dom";
import { LISILiveSetupPageV2 } from "./lisi-components/LISILiveSetupPageV2";
import { CircleProgressBar } from "../../components/progressbar/CircleProgressBar";
import { LISITopProgressBars } from "./lisi-components/LISITopProgressBars";

const LIVE_SETUP_STORAGE_KEY = "lisi_live_selectedWorkOrder_v1";
const LIVE_WATCHDOG_STORAGE_KEY = 'lisi_live_watchdog_status';

const readLiveWatchdogEnabledFromStorage = () => {
  return localStorage.getItem(LIVE_WATCHDOG_STORAGE_KEY) !== 'disabled';
};

const writeLiveWatchdogEnabledToStorage = (enabled) => {
  localStorage.setItem(LIVE_WATCHDOG_STORAGE_KEY, enabled ? 'enabled' : 'disabled');
};

const readLiveSetupFromStorage = () => {
  try {
    const raw = localStorage.getItem(LIVE_SETUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const LISILivePage1 = ({ currentData, selectedWorkOrder }) => {

  const criteria = currentData?.criteria !== undefined ? currentData.criteria : [];
  const pieceHasAction = currentData?.piece_action?.[0] > 0;

  return (
    <>
      {/* Title absolute position - Not clean */}
      <div className="text-lg text-white font-semibold fixed top-0 left-0 w-full h-8 flex items-center justify-center text-center">
        Zone enfournement
      </div>

      {/* Main part*/}
      <div className="w-full flex flex-row-reverse h-full flex-1 gap-4 overflow-y-auto">

        {/* Left part */}
        <div className="w-1/3 h-full flex flex-col justify-between gap-4">

          <div className="h-full flex flex-col justify-between gap-4 bg-gray-200 p-4 rounded-md">
            {/* Piece action*/}
            <h1 className={`h-full flex items-center justify-center text-center ${pieceHasAction && 'bg-perception-success-300 text-white'} rounded-md ${pieceHasAction ? 'text-8xl font-semibold' : ''}`}>
              {
                currentData?.piece_action?.[0] === 1 ? 'Pièce chargée' :
                currentData?.piece_action?.[0] === 2 ? 'Pièce déchargée' :
                currentData?.piece_action?.[0] === 3 ? 'Pièce frappée' :
                currentData?.piece_action?.[0] === 4 ? 'Pièce sortie' : 'Aucune action en cours'
              }
            </h1>

          </div>
          {/* AI criteria */}
          <LISITopProgressBars criteria={criteria} />

          {/* Logos */}
          <div className="p-8 flex justify-center items-center text-xl gap-4 text-black">
            <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-[60px]" />
            x
            <img src={LogoLISI} alt="logo-LCA" className="max-w-[200px] max-h-[60px]" />
          </div>
        </div>

        {/* Main part */}
        <div className="w-2/3 h-full flex flex-col gap-4">

          {/* Stats */}
          <div className="bg-gray-200 flex rounded-md p-4 gap-4">
            {/* Pieces number */}
            <div className="p-4 w-1/4 relative rounded-md flex border-4 border-gray-300 items-center">
              <CircleProgressBar
                text={"Pièces dans le four"}
                value={currentData?.pieces_counts?.oven_pieces ?? 0}
                textColor="text-perception-gray-700" valueColor="text-perception-gray-700"
                unit={null} hideStroke={true} size={120}
              />
            </div>

            {/* Wasted pieces number */}
            <div className="p-4 w-1/4 relative rounded-md flex border-4 border-gray-300 items-center">
              <CircleProgressBar
                text={"Pièces rebutées"}
                value={currentData?.pieces_counts?.wasted_pieces ?? 0}
                textColor="text-perception-gray-700" valueColor="text-perception-gray-700"
                unit={null} hideStroke={true} size={120}
              />
            </div>

            {/* Forged pieces count */}
            <div className="flex items-center p-4 w-1/4 border-4 border-gray-300 rounded-md">
              <CircleProgressBar
                text={"Pièces frappées"}
                value={currentData?.pieces_counts?.forged_pieces ?? 0} maxValue={selectedWorkOrder.quantity}
                textColor="text-perception-gray-700" bgColor="stroke-gray-300"
                valueColor="text-perception-gray-700" unit={null} size={120}
              />
            </div>

            {/* Finished pieces time */}
            <div className="flex items-center p-4 w-1/4 border-4 border-gray-300 rounded-md">
              <CircleProgressBar
                text={"Pièces finies"}
                value={currentData?.pieces_counts?.finished_pieces ?? 0} maxValue={selectedWorkOrder.quantity}
                textColor="text-perception-gray-700" bgColor="stroke-gray-300"
                valueColor="text-perception-gray-700" unit={null} size={120}
              />
            </div>
          </div>

          {/* Heater durations */}
          {/*<div className="bg-perception-blue px-8 py-4 text-white rounded-md">Durée de chauffe</div>*/}
          <div className="bg-gray-200 h-full rounded-md overflow-y-auto">
            {currentData?.oven_pieces?.length > 0 ?
              <div className="grid grid-cols-4 flex-1 gap-4 p-4 content-start">
                {currentData?.oven_pieces?.map((piece, index) => {
                  const progressPercent = Math.round(piece.oven_time / selectedWorkOrder.oven_time * 100);
                  const isOverTime = piece.oven_time >= selectedWorkOrder.oven_time + selectedWorkOrder.oven_tolerance;
                  const isNearEnd = piece.oven_time >= selectedWorkOrder.oven_time + selectedWorkOrder.oven_tolerance / 2;
                  const isEnded = piece.oven_time >= selectedWorkOrder.oven_time;

                  const circlecolor = isOverTime ? 'bg-perception-error-500' : isNearEnd ? 'bg-perception-warn-500' :
                    isEnded ? 'bg-perception-success-600' : 'bg-perception-gray-500';

                  const borderColor = isOverTime ? 'border-perception-error-500' : isNearEnd ? 'border-perception-warn-500' :
                    isEnded ? 'border-perception-success-600' : '';

                  return (
                    <div key={index} className={`${borderColor} border-4 p-3 bg-white rounded-md flex flex-col gap-2 justify-between`}>

                      <div className="flex gap-2 items-center justify-between">
                        {/*<div className="flex gap-2 items-center justify-start">*/}
                        {/*  /!* Circle icon *!/*/}
                        {/*  <div className={`${circlecolor} rounded-full min-w-5 size-5`}/>*/}
                        {/*  <div className="text-lg">Temps de chauffe</div>*/}
                        {/*</div>*/}

                        {/* Progressbar */}
                        <div className="w-full flex h-3 gap-2 items-center">
                          <div className="w-full flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${circlecolor}`} style={{ width: `${progressPercent}%`  }}/>
                          </div>
                          <div className="text-base w-10 text-center">{Math.min(Math.round(progressPercent), 100)}%</div>
                        </div>

                        <div className="bg-perception-gray-100 rounded-md px-2 py-1 text-base">#{piece.id}</div>
                      </div>

                      <div className="flex items-center justify-between text-lg gap-4">
                        <div><span className="text-nowrap">Temps de chauffe</span> <span className="text-2xl">{formatSecondsToMinutesSeconds(piece.oven_time)}</span></div>
                        <div>Fin estimée <span className="text-2xl">{formatSecondsToHoursMinutesSeconds(piece.entry_oven_time + selectedWorkOrder.oven_time)}</span></div>
                      </div>

                    </div>
                  )
                })}
              </div>
              :
              <div className="h-full flex items-center justify-center m-0 text-center text-perception-gray-500">
                Aucune pièce dans le four actuellement.
              </div>
            }
          </div>
          {/* End heater durations */}

        </div>
        {/* End main part */}

      </div>
    </>
  );
};


export const LISILivePage2 = ({ currentData }) => {
  return (
    <>
      {/* Header */}
      <h1 className="text-center p-4 font-bold bg-perception-blue text-white rounded-md">Zone ébavurage</h1>

      {/* Main part*/}
      <div className="w-full flex h-full flex-1 gap-4 overflow-y-auto">

        {/* Left part */}
        <div className="w-full h-full flex flex-col justify-between gap-4">

          <div className="flex flex-col justify-start gap-4">
            {/* Pieces number */}
            <div className="h-1/3 p-8 relative rounded-md flex bg-gray-200 items-center">
              Pièces ébavurées : {currentData?.pieces_counts?.cut_pieces ?? '-'}
            </div>

            {/* Forged pieces count */}
            <div className="h-1/3 flex items-center p-8 bg-gray-200 rounded-md">
              Pièces finies : {currentData?.pieces_counts?.finished_pieces ?? '-'}
            </div>

            {/* Finished pieces time */}
            <div className="h-1/3 flex items-center p-8 bg-gray-200 rounded-md">
              Pièces rebutées : {currentData?.pieces_counts?.wasted_pieces ?? '-'}
            </div>
          </div>

          {/* Logos */}
          <div className="p-8 flex justify-center items-center text-xl gap-4 text-black">
            <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-[60px]" />
            x
            <img src={LogoLISI} alt="logo-LCA" className="max-w-[200px] max-h-[60px]" />
          </div>
        </div>
        {/* End heater durations */}

      </div>
      {/* End main part */}
    </>
  );
};

export const LISILivePage = ({ isSetup=false }) => {

  const { socket, subscribeToEvent, unsubscribeFromEvent, sendEvent } = useSocket();

  const [currentData, setCurrentData] = useState({});
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  const [isConnectionLost, setIsConnectionLost] = useState(false);
  const [isWatchdogEnabled, setIsWatchdogEnabled] = useState(readLiveWatchdogEnabledFromStorage);
  const lastCurrentDataAt = useRef(Date.now());
  const reloadTimeout = useRef(null);
  const currentDataTimeoutMs = 12 * 1000;
  const reloadDelayMs = 10 * 1000;

  useEffect(() => {
    const saved = readLiveSetupFromStorage();
    if (!saved?.selectedWorkOrder) return;
    setSelectedWorkOrder(saved.selectedWorkOrder);
  }, []);

  useEffect(() => {
    if (!socket) return;
    sendEvent('current_data', { 'get_production': 1});
  }, [socket]);

  const lockedData = useRef(false);

  const { screen } = useParams();
  const screenId = screen ? parseInt(screen) : 1;

  const handleConnectionLoss = () => {
    if (!isWatchdogEnabled) return;
    if (document.visibilityState !== 'visible') return;
    if (reloadTimeout.current) return;

    setIsConnectionLost(true);

    reloadTimeout.current = setTimeout(() => {
      window.location.reload();
    }, reloadDelayMs);
  };

  const checkCurrentDataWatchdog = () => {
    if (!isWatchdogEnabled) return;
    // console.log('[DEBUG] Check');
    if (document.visibilityState !== 'visible') return;
    // console.log('[DEBUG] Visible');

    const elapsed = Date.now() - lastCurrentDataAt.current;

    if (elapsed > currentDataTimeoutMs) {
      // console.log('[DEBUG] Over');
      handleConnectionLoss();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.repeat) return;
      if (!event.ctrlKey || !event.altKey || event.key.toLowerCase() !== 'h') return;

      event.preventDefault();

      const nextValue = !readLiveWatchdogEnabledFromStorage();

      writeLiveWatchdogEnabledToStorage(nextValue);
      setIsWatchdogEnabled(nextValue);

      if (!nextValue) {
        setIsConnectionLost(false);

        if (reloadTimeout.current) {
          clearTimeout(reloadTimeout.current);
          reloadTimeout.current = null;
        }
      }

      alert(nextValue ? 'Watchdog activé.' : 'Watchdog désactivé.');
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleReceivedData = (data) => {
      console.log(data);
      lastCurrentDataAt.current = Date.now();
      setIsConnectionLost(false);

      if (reloadTimeout.current) {
        clearTimeout(reloadTimeout.current);
        reloadTimeout.current = null;
      }

      if (lockedData.current) return;
      setCurrentData(prevData => ({...prevData, ...data}));

      // Start the interface
      if (data?.start_production !== undefined) {
        setSelectedWorkOrder(data.start_production);
      }
      else if (data?.stop_production !== undefined) {
        setSelectedWorkOrder(null);
      }
    };

    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket]);

  useEffect(() => {
    const interval = setInterval(() => {
      checkCurrentDataWatchdog();
    }, 5000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkCurrentDataWatchdog();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (reloadTimeout.current) {
        clearTimeout(reloadTimeout.current);
      }
    };
  }, [isWatchdogEnabled]);

  if (isSetup && screenId === 2) return <LISILiveSetupPageV2/>
  if (isSetup && screenId) return <LISILiveSetupPage/>

  const alertFullScreen = !(currentData?.alert_timer >= 3);

  return (
    <div className='h-full bg-gray-100 relative'>
      {isConnectionLost &&
        <div className="w-full h-32 backdrop-blur-md absolute bottom-0 left-0 z-[60] bg-black/70 flex justify-center items-center text-white text-center text-3xl">
          Reconnexion en cours.<br/>
          L'application va être rechargée dans 10 secondes.
        </div>
      }
      {
        (currentData?.alert_message && (screenId === currentData?.alert_screen || currentData?.alert_screen === null)) &&
        <div className={`w-full backdrop-blur-md absolute bottom-0 left-0 z-50 bg-perception-error-500/80 flex justify-center items-center
                        text-white text-center transition-all overflow-hidden shadow
                        ${alertFullScreen ? 'h-full' : 'h-32'} ${alertFullScreen ? 'text-7xl' : 'text-5xl'} ${alertFullScreen ? 'shadow-transparent' : 'shadow-black'}`}>
          <div className="w-4/5 ">
            {currentData?.alert_message}
          </div>
        </div>
      }

      {
        currentData?.pieces_counts_synthesis &&
          <div className="w-full h-full backdrop-blur-md absolute top-0 left-0 z-10 bg-perception-blue/80 flex justify-center items-center">
            <div className="w-4/5 relative bg-white h-4/5 flex flex-col justify-between rounded-md overflow-hidden">
              {/*<div className="absolute top-0 right-0 cursor-pointer" onClick={(e) => handleCloseSynthesis(e)}>*/}
              {/*  <XMarkIcon className="w-16 h-16 text-gray-600 m-4"/>*/}
              {/*</div>*/}
              <h1 className="w-full bg-gray-200 text-center text-3xl p-8">Synthèse de la production</h1>
              <div className="w-full h-full flex justify-center items-center overflow-auto">
                <div className="w-full h-full overflow-auto p-4 flex justify-center items-center">
                  <table className="w-[700px] text-3xl">
                    <tbody>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces chauffées</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.heated_pieces || 0}</td>
                      </tr>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces frappées</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.forged_pieces || 0}</td>
                      </tr>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces ébavurées</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.cut_pieces || 0}</td>
                      </tr>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces finies</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.finished_pieces || 0}</td>
                      </tr>
                      <tr>
                        <td className="font-semibold w-3/4 pb-8">Nombre de pièces rebutées</td>
                        <td className="text-right w-1/4">{currentData?.pieces_counts_synthesis?.wasted_pieces || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
      }

      {
        selectedWorkOrder !== null ?
          <div className="w-full h-full flex flex-col justify-start text-5xl text-perception-gray-700 p-4 gap-4 rounded-md">

            {
              screenId === 1 ?
                <LISILivePage1 currentData={currentData} selectedWorkOrder={selectedWorkOrder}/>
              :
                <LISILivePage2 currentData={currentData}/>
            }

          </div>
          :
          <div className="h-full flex flex-col gap-16 justify-center items-center text-center text-5xl text-perception-gray-500">
            <WrenchScrewdriverIcon className="size-64"/>
            <p>Pour démarrer, sélectionnez une référence sur le pupitre.</p>
          </div>
      }
    </div>
  );
};
